import React, { useState, useEffect, useRef } from 'react';
import { MdLink, MdExpandMore, MdExpandLess } from 'react-icons/md';

const RelationshipForm = ({ erdState, currentRelationship, setCurrentRelationship, editingMode, setEditingMode, onSaveEvent }) => {
  const [expandedParticipants, setExpandedParticipants] = useState({});
  const [expandedAttributes, setExpandedAttributes] = useState({});
  const [pendingSaveEvent, setPendingSaveEvent] = useState(null);
  const inputTextRef = useRef(erdState.inputText);

  // Toggle individual participant expansion
  const toggleParticipantExpansion = (index) => {
    setExpandedParticipants(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Toggle individual attribute expansion
  const toggleAttributeExpansion = (index) => {
    setExpandedAttributes(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Add participant to relationship
  const addRelationshipParticipant = () => {
    // Insert new participant at the beginning instead of the end
    setCurrentRelationship(prev => ({
      ...prev,
      participants: [{ entity: '', cardinality: '1', roleName: '', hasTotalParticipation: false }, ...prev.participants]
    }));

    // Shift all existing expansion indices up by 1
    setExpandedParticipants(prev => {
      const newState = {};
      Object.keys(prev).forEach(key => {
        const numKey = parseInt(key);
        newState[numKey + 1] = prev[numKey];
      });
      // Auto-expand new participant at index 0
      newState[0] = true;
      return newState;
    });
  };

  // Remove participant from relationship
  const removeRelationshipParticipant = (index) => {
    setCurrentRelationship(prev => ({
      ...prev,
      participants: prev.participants.filter((_, i) => i !== index)
    }));

    // Clean up expansion state with proper index adjustment
    setExpandedParticipants(prev => {
      const newState = { ...prev };
      delete newState[index];

      // Shift indices down for participants that were after the removed one
      Object.keys(newState).forEach(key => {
        const numKey = parseInt(key);
        if (numKey > index) {
          newState[numKey - 1] = newState[numKey];
          delete newState[numKey];
        }
      });
      return newState;
    });
  };

  // Update relationship participant
  const updateRelationshipParticipant = (index, field, value) => {
    setCurrentRelationship(prev => ({
      ...prev,
      participants: prev.participants.map((part, i) =>
        i === index ? { ...part, [field]: value } : part
      )
    }));
  };

  // Add relationship attribute
  const addRelationshipAttribute = () => {
    // Insert new attribute at the beginning instead of the end
    setCurrentRelationship(prev => ({
      ...prev,
      attributes: [{ name: '', subAttributes: '' }, ...prev.attributes]
    }));

    // Shift all existing expansion indices up by 1
    setExpandedAttributes(prev => {
      const newState = {};
      Object.keys(prev).forEach(key => {
        const numKey = parseInt(key);
        newState[numKey + 1] = prev[numKey];
      });
      // Auto-expand new attribute at index 0
      newState[0] = true;
      return newState;
    });
  };

  // Remove relationship attribute
  const removeRelationshipAttribute = (index) => {
    setCurrentRelationship(prev => ({
      ...prev,
      attributes: prev.attributes.filter((_, i) => i !== index)
    }));

    // Clean up expansion state with proper index adjustment
    setExpandedAttributes(prev => {
      const newState = { ...prev };
      delete newState[index];

      // Shift indices down for attributes that were after the removed one
      Object.keys(newState).forEach(key => {
        const numKey = parseInt(key);
        if (numKey > index) {
          newState[numKey - 1] = newState[numKey];
          delete newState[numKey];
        }
      });
      return newState;
    });
  };

  // Update relationship attribute
  const updateRelationshipAttribute = (index, field, value) => {
    setCurrentRelationship(prev => ({
      ...prev,
      attributes: prev.attributes.map((attr, i) =>
        i === index ? { ...attr, [field]: value } : attr
      )
    }));
  };

  // Generate or update relationship notation
  const generateRelationshipNotation = () => {
    if (!currentRelationship.name.trim()) {
      alert('Please enter a relationship name');
      return;
    }

    const validParticipants = currentRelationship.participants.filter(p => p.entity.trim() !== '');
    if (validParticipants.length < 2) {
      alert('Please select at least 2 entities for the relationship');
      return;
    }

    const relationshipTypes = currentRelationship.isIdentifying ? ' @identifying' : '';
    let relationshipNotation = `[Relationship]\n${currentRelationship.name}${relationshipTypes}\n--`;

    relationshipNotation += '\n';
    validParticipants.forEach(participant => {
      const participationTypes = participant.hasTotalParticipation ? ' @total_participation' : '';
      const roleStr = participant.roleName?.trim() ? `:${participant.roleName.trim()}` : '';
      relationshipNotation += `${participant.entity}${roleStr} (${participant.cardinality})${participationTypes}\n`;
    });

    const validAttributes = currentRelationship.attributes.filter(attr => attr.name.trim() !== '');
    if (validAttributes.length > 0) {
      relationshipNotation += '--\n';
      validAttributes.forEach(attr => {
        let attrLine = attr.name;
        if (attr.subAttributes.trim()) {
          attrLine += ` (${attr.subAttributes})`;
        }
        relationshipNotation += `${attrLine}\n`;
      });
    }

    // Check if we're editing an existing component
    if (editingMode && editingMode.isEditing && editingMode.componentType === 'relationship' && editingMode.originalComponent) {
      const originalName = editingMode.originalComponent.name;

      // Split the notation into lines for precise replacement
      const lines = erdState.inputText.split('\n');
      const newLines = [];
      let i = 0;
      let replaced = false;

      while (i < lines.length) {
        const line = lines[i];

        // Look for [Relationship] followed by the original relationship name
        if (line.trim() === '[Relationship]' && i + 1 < lines.length) {
          const nextLine = lines[i + 1].trim();
          const nameMatch = nextLine.match(/^(\w+)/);

          if (nameMatch && nameMatch[1] === originalName) {
            // Found the relationship to replace - add the new notation
            newLines.push(...relationshipNotation.split('\n'));
            replaced = true;

            // Skip all lines until we hit the next [Section] or end of file
            i += 2; // Skip [Relationship] and name line
            while (i < lines.length && !lines[i].trim().startsWith('[')) {
              i++;
            }
            continue; // Don't increment i again since we're already at the next section
          }
        }

        newLines.push(line);
        i++;
      }

      if (replaced) {
        erdState.setInputText(newLines.join('\n'));
        console.log('Relationship successfully replaced');
        // Set pending save event for after state update
        setPendingSaveEvent('relationship_form_submitted');
      } else {
        console.error('Could not find relationship to replace, adding as new');
        erdState.setInputText(prev => prev + (prev ? '\n\n' : '') + relationshipNotation);
        // Set pending save event for after state update
        setPendingSaveEvent('relationship_form_submitted');
      }

      // Clear editing mode
      setEditingMode({
        isEditing: false,
        componentType: null,
        originalComponent: null
      });
    } else {
      // Add new component
      erdState.setInputText(prev => prev + (prev ? '\n\n' : '') + relationshipNotation);
      // Set pending save event for after state update
      setPendingSaveEvent('relationship_added');
    }

    // Reset form after successful addition/edit
    setCurrentRelationship({
      name: '',
      isIdentifying: false,
      participants: [{ entity: '', cardinality: '1', roleName: '', hasTotalParticipation: false }],
      attributes: [{ name: '', subAttributes: '' }]
    });
  };

  // Effect to track inputText changes and trigger pending save events
  useEffect(() => {
    // Only trigger save if inputText actually changed and we have a pending event
    if (erdState.inputText !== inputTextRef.current && pendingSaveEvent && onSaveEvent) {
      inputTextRef.current = erdState.inputText;
      // Use setTimeout to ensure the state has fully propagated
      setTimeout(() => {
        onSaveEvent(pendingSaveEvent);
        setPendingSaveEvent(null);
      }, 0);
    } else {
      inputTextRef.current = erdState.inputText;
    }
  }, [erdState.inputText, pendingSaveEvent, onSaveEvent]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">
          {editingMode && editingMode.isEditing && editingMode.componentType === 'relationship'
            ? `Edit Relationship: ${editingMode.originalComponent?.name || 'Unknown'}`
            : 'Add Relationship'}
        </h2>
        <MdLink className="w-6 h-6 text-gray-600" />
      </div>

      {/* Relationship Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Relationship Name</label>
        <input
          type="text"
          placeholder="e.g., works_for, manages"
          value={currentRelationship.name}
          onChange={(e) => setCurrentRelationship(prev => ({ ...prev, name: e.target.value }))}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Relationship Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Relationship Type</label>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={currentRelationship.isIdentifying}
            onChange={(e) => setCurrentRelationship(prev => ({ ...prev, isIdentifying: e.target.checked }))}
            className="mr-2 rounded"
          />
          <span className="text-sm">Identifying Relationship</span>
        </label>
      </div>

      {/* Participants */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Participants ({currentRelationship.participants.filter(p => p.entity.trim() !== '').length})
          </label>
          <button
            onClick={addRelationshipParticipant}
            className="text-blue-500 hover:text-blue-700 text-sm font-medium"
          >
            + Add Participant
          </button>
        </div>

        <div className="space-y-4">
          {currentRelationship.participants.map((participant, index) => {
            const isExpanded = expandedParticipants[index] !== false;

            return (
              <div key={index} className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden transition-all duration-200 mb-1">
                {/* Participant Header */}
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                  onClick={() => toggleParticipantExpansion(index)}
                >
                  <div className="flex items-center flex-1">
                    <span className="text-sm font-medium text-gray-700">
                      {participant.entity.trim() !== '' ? participant.entity : `Participant ${index + 1}`}
                    </span>
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1 rounded">
                      ({participant.cardinality})
                    </span>
                    {participant.roleName?.trim() && (
                      <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1 rounded">{participant.roleName.trim()}</span>
                    )}
                    {participant.hasTotalParticipation && (
                      <span className="ml-1 text-xs bg-green-100 text-green-700 px-1 rounded">Total</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {currentRelationship.participants.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeRelationshipParticipant(index);
                        }}
                        className="text-red-500 hover:text-red-700 text-xs px-2 py-1 transition-colors duration-150"
                      >
                        Remove
                      </button>
                    )}
                    <div className="transition-transform duration-200">
                      {isExpanded ? (
                        <MdExpandLess className="w-4 h-4 text-gray-500" />
                      ) : (
                        <MdExpandMore className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Participant Content */}
                <div className={`transition-all duration-200 ease-in-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                  <div className="px-3 pb-3 pt-1 space-y-3">
                    {/* Entity Selection */}
                    <select
                      value={participant.entity}
                      onChange={(e) => updateRelationshipParticipant(index, 'entity', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 transition-all duration-150"
                    >
                      <option value="">Select entity...</option>
                      {erdState.availableEntities?.map((entity, idx) => (
                        <option key={idx} value={entity}>{entity}</option>
                      ))}
                    </select>

                    {/* Cardinality */}
                    <select
                      value={participant.cardinality}
                      onChange={(e) => updateRelationshipParticipant(index, 'cardinality', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 transition-all duration-150"
                    >
                      <option value="1">1 (One)</option>
                      <option value="N">N (Many)</option>
                      <option value="M">M (Many)</option>
                      <option value="P">P (Many)</option>
                    </select>

                    {/* Role Name (optional) */}
                    <input
                      type="text"
                      placeholder="Role name (optional, e.g. supervisor)"
                      value={participant.roleName || ''}
                      onChange={(e) => updateRelationshipParticipant(index, 'roleName', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 transition-all duration-150"
                    />

                    {/* Total Participation */}
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={participant.hasTotalParticipation}
                        onChange={(e) => updateRelationshipParticipant(index, 'hasTotalParticipation', e.target.checked)}
                        className="mr-2 rounded transition-all duration-150"
                      />
                      Total Participation
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Relationship Attributes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Relationship Attributes ({currentRelationship.attributes.filter(attr => attr.name.trim() !== '').length})
          </label>
          <button
            onClick={addRelationshipAttribute}
            className="text-blue-500 hover:text-blue-700 text-sm font-medium"
          >
            + Add Attribute
          </button>
        </div>

        <div className="space-y-4">
          {currentRelationship.attributes.map((attr, index) => {
            const isExpanded = expandedAttributes[index] !== false;

            return (
              <div key={index} className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden transition-all duration-200 mb-1">
                {/* Attribute Header */}
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                  onClick={() => toggleAttributeExpansion(index)}
                >
                  <div className="flex items-center flex-1">
                    <span className="text-sm font-medium text-gray-700">
                      {attr.name.trim() !== '' ? attr.name : `Attribute ${index + 1}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {currentRelationship.attributes.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeRelationshipAttribute(index);
                        }}
                        className="text-red-500 hover:text-red-700 text-xs px-2 py-1 transition-colors duration-150"
                      >
                        Remove
                      </button>
                    )}
                    <div className="transition-transform duration-200">
                      {isExpanded ? (
                        <MdExpandLess className="w-4 h-4 text-gray-500" />
                      ) : (
                        <MdExpandMore className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Attribute Content */}
                <div className={`transition-all duration-200 ease-in-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                  <div className="px-3 pb-3 pt-1 space-y-3">
                    {/* Attribute Name */}
                    <input
                      type="text"
                      placeholder="Attribute name"
                      value={attr.name}
                      onChange={(e) => updateRelationshipAttribute(index, 'name', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 transition-all duration-150"
                    />

                    {/* Sub-attributes */}
                    <input
                      type="text"
                      placeholder="Sub-attributes (e.g., street,city,state)"
                      value={attr.subAttributes}
                      onChange={(e) => updateRelationshipAttribute(index, 'subAttributes', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 transition-all duration-150"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add/Edit Relationship Button */}
      <button
        onClick={generateRelationshipNotation}
        className="w-full bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-md font-medium transition-colors duration-200"
      >
        {editingMode && editingMode.isEditing && editingMode.componentType === 'relationship'
          ? 'Confirm Edits'
          : 'Add Relationship to Notation'}
      </button>
    </div>
  );
};

export default RelationshipForm;